import {
  formatQuery,
  type Field,
  type RuleGroupType,
  type RuleType,
} from "react-querybuilder";
import { formatConceptCodeToLabel } from "../../../services/autoTaggingService";

const CONDITION_OPERATORS = [
  { name: "containsAny", label: "contains any of" },
  { name: "containsAll", label: "contains all of" },
  { name: "doesNotContainAny", label: "does not contain any of" },
] as const;

function isRuleGroup(item: unknown): item is RuleGroupType {
  return Boolean(item && typeof item === "object" && "rules" in item);
}

function formatValueItem(item: unknown): string {
  if (item == null) return "";
  if (
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "boolean"
  ) {
    return String(item).trim();
  }
  if (typeof item === "object") {
    const record = item as Record<string, unknown>;
    // Prefer display name; fall back to code for API payloads shaped as { name, code }.
    const name = String(
      record.name ?? record.label ?? record.display_name ?? "",
    ).trim();
    if (name) return name;
    return String(record.code ?? record.value ?? "").trim();
  }
  return String(item).trim();
}

function formatLeafValue(value: unknown): string {
  if (Array.isArray(value)) {
    const labels = value.map(formatValueItem).filter(Boolean);
    if (labels.length === 1 && labels[0] === "*") return "All";
    return labels.join(", ");
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.allValues === true) return "All";
    if (Array.isArray(record.conceptValues)) {
      return formatLeafValue(record.conceptValues);
    }
  }
  if (value == null) return "";
  const text = formatValueItem(value);
  return text === "*" ? "All" : text;
}

function collectFields(query: RuleGroupType): Field[] {
  const labels = new Map<string, string>();

  const walk = (group: RuleGroupType) => {
    for (const item of group.rules) {
      if (isRuleGroup(item)) {
        walk(item);
        continue;
      }
      const field = String((item as RuleType).field ?? "").trim();
      if (!field || labels.has(field)) continue;
      labels.set(field, formatConceptCodeToLabel(field) || field);
    }
  };

  walk(query);
  return Array.from(labels.entries()).map(([name, label]) => ({ name, label }));
}

/** Convert API condition payload into a RuleGroupType usable by formatQuery. */
export function conditionPayloadToQuery(
  condition: unknown,
): RuleGroupType | null {
  if (!isRuleGroup(condition)) return null;

  const mapGroup = (group: RuleGroupType): RuleGroupType => ({
    combinator: String(group.combinator ?? "and"),
    not: Boolean(group.not),
    rules: (group.rules ?? []).map((item) => {
      if (isRuleGroup(item)) return mapGroup(item);
      const rule = item as RuleType;
      return {
        field: String(rule.field ?? "").trim(),
        operator: String(rule.operator ?? "").trim(),
        value: formatLeafValue(rule.value),
        valueSource: "value" as const,
      };
    }),
  });

  return mapGroup(condition);
}

/** Turn a conditional-plan condition into a natural-language description. */
export function formatConditionToNaturalLanguage(condition: unknown): string {
  const query = conditionPayloadToQuery(condition);
  if (!query || query.rules.length === 0) return "";

  try {
    return String(
      formatQuery(query, {
        format: "natural_language",
        getOperators: () => [...CONDITION_OPERATORS],
        fields: collectFields(query),
      }),
    ).trim();
  } catch {
    return "";
  }
}

function resolveFieldLabel(
  field: string,
  fieldLabelByCode?: ReadonlyMap<string, string> | Record<string, string>,
): string {
  const trimmed = field.trim();
  if (!trimmed) return "";
  if (fieldLabelByCode instanceof Map) {
    const mapped = fieldLabelByCode.get(trimmed)?.trim();
    if (mapped) return mapped;
    return formatConceptCodeToLabel(trimmed) || trimmed;
  }
  if (fieldLabelByCode) {
    const mapped = String(
      (fieldLabelByCode as Record<string, string>)[trimmed] ?? "",
    ).trim();
    if (mapped) return mapped;
  }
  return formatConceptCodeToLabel(trimmed) || trimmed;
}

function resolveOperatorLabel(operator: string): string {
  const trimmed = operator.trim();
  const matched = CONDITION_OPERATORS.find((item) => item.name === trimmed);
  return matched?.label ?? trimmed;
}

function formatSidebarLeaf(
  rule: RuleType,
  fieldLabelByCode?: ReadonlyMap<string, string> | Record<string, string>,
): string {
  const fieldLabel = resolveFieldLabel(String(rule.field ?? ""), fieldLabelByCode);
  const valueLabel = formatLeafValue(rule.value);
  if (!fieldLabel || !valueLabel) return "";
  const operatorLabel = resolveOperatorLabel(String(rule.operator ?? ""));
  if (!operatorLabel) return `${fieldLabel} ${valueLabel}`;
  return `${fieldLabel} ${operatorLabel} ${valueLabel}`;
}

function formatSidebarGroup(
  group: RuleGroupType,
  fieldLabelByCode?: ReadonlyMap<string, string> | Record<string, string>,
): string {
  const combinator = String(group.combinator ?? "and").toLowerCase() === "or" ? "or" : "and";
  const parts = (group.rules ?? [])
    .map((item) => {
      if (isRuleGroup(item)) return formatSidebarGroup(item, fieldLabelByCode);
      return formatSidebarLeaf(item as RuleType, fieldLabelByCode);
    })
    .filter(Boolean);
  return parts.join(` ${combinator} `);
}

/**
 * Compact sidebar summary using operator English labels, e.g.
 * "When Product Type contains any of Large Molecule and Topic contains any of Safety Concern".
 * Prefers value/field display names over codes.
 */
export function formatConditionSidebarSummary(
  condition: unknown,
  fieldLabelByCode?: ReadonlyMap<string, string> | Record<string, string>,
): string {
  if (!isRuleGroup(condition)) return "";
  // Editor queries store EnhancedTaggingConditionValue objects; API payloads
  // may store string/object arrays — formatLeafValue handles both.
  const body = formatSidebarGroup(condition, fieldLabelByCode).trim();
  if (!body) return "";
  return `When ${body}`;
}
